import React, { useState } from 'react';
import './ProtectedAssetsModal.css';

// ─── Helper: group coins by coinType, keep non-coins individual ────────────
function groupProtectedAssets(assets) {
  const coinGroups = {};
  const nonCoins = [];

  assets.forEach(obj => {
    if (obj.enriched?.isCoin && obj.enriched?.coinType) {
      const key = obj.enriched.coinType;
      if (!coinGroups[key]) {
        coinGroups[key] = {
          coinType: key,
          symbol: obj.enriched.symbol || key.split('::').pop(),
          displayName: obj.enriched.displayName || obj.enriched.symbol || key.split('::').pop(),
          iconUrl: obj.enriched.iconUrl || null,
          objects: [],
          totalBalance: 0,
          decimals: obj.enriched.metadata?.decimals ?? 9,
          filterCategory: obj.filterCategory,
          filterInfo: obj.filterInfo,
          usdValue: 0,
          tokenPrice: obj.tokenPrice || 0,
        };
      }
      coinGroups[key].objects.push(obj);
      coinGroups[key].totalBalance += parseFloat(obj.enriched.rawBalance || 0);
      coinGroups[key].usdValue += obj.usdValue || 0;
      if (!coinGroups[key].tokenPrice && obj.tokenPrice) coinGroups[key].tokenPrice = obj.tokenPrice;
      if (!coinGroups[key].iconUrl && obj.enriched?.iconUrl) coinGroups[key].iconUrl = obj.enriched.iconUrl;
    } else {
      nonCoins.push(obj);
    }
  });

  const grouped = Object.values(coinGroups).map(g => ({
    ...g,
    isGroup: true,
    objectCount: g.objects.length,
    formattedBalance: (g.totalBalance / Math.pow(10, g.decimals)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }),
  }));

  return [...grouped, ...nonCoins];
}

function getAssetName(asset) {
  if (asset.isGroup) return asset.displayName || asset.symbol || 'Unknown Token';
  if (asset.enriched?.isCoin) return asset.enriched.displayName || asset.enriched.symbol || 'Unknown Token';
  if (asset.enriched?.displayName) return asset.enriched.displayName;
  if (asset.data?.display?.data?.name) return asset.data.display.data.name;
  if (asset.data?.type) return asset.data.type.split('::').pop() || 'Unknown Object';
  return 'Unknown Object';
}

function getAssetSubtitle(asset) {
  if (asset.isGroup) {
    const countLabel = asset.objectCount > 1 ? ` (${asset.objectCount} objects)` : '';
    return `${asset.formattedBalance} ${asset.symbol}${countLabel}`;
  }
  if (asset.enriched?.isCoin) return `${asset.enriched.formattedBalance} ${asset.enriched.symbol}`;
  return null;
}

function getAssetIcon(asset) {
  if (asset.isGroup) return asset.iconUrl || null;
  // For coins: use enriched iconUrl only (small token icon)
  // For non-coins (staking, DeFi, NFTs): suppress image_url — renders as huge NFT art
  if (asset.enriched?.isCoin) return asset.enriched?.iconUrl || null;
  return null;
}

function formatUsd(value) {
  if (!value || value <= 0) return null;
  if (value < 0.01) return '<$0.01';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Main Modal — READ ONLY. Whitelist management lives in the Whitelist Panel. ──
function ProtectedAssetsModal({ protectedAssets, filterStats, onClose, isToiletMode }) {

  // Only show system-auto-protected — whitelisted items are managed in Whitelist Panel
  const autoProtected = protectedAssets.filter(obj => obj.filterCategory !== 'whitelisted');
  const allGrouped = groupProtectedAssets(autoProtected);

  const groupedAssets = {
    safeValue:    allGrouped.filter(obj => obj.filterCategory === 'safe_value'),
    lpTokens:     allGrouped.filter(obj => obj.filterCategory === 'valuable_object' && obj.filterInfo?.reason?.toLowerCase().includes('lp')),
    staking:      allGrouped.filter(obj => obj.filterCategory === 'valuable_object' && obj.filterInfo?.reason?.toLowerCase().includes('stak')),
    other:        allGrouped.filter(obj => obj.filterCategory === 'valuable_object' && !obj.filterInfo?.reason?.toLowerCase().includes('lp') && !obj.filterInfo?.reason?.toLowerCase().includes('stak')),
    nativeSui:    allGrouped.filter(obj => obj.filterCategory === 'native_sui'),
    distilObject: allGrouped.filter(obj => obj.filterCategory === 'distil_object'),
  };

  const seen = new Set();
  const totalValue = allGrouped.reduce((sum, obj) => {
    const key = obj.isGroup ? obj.coinType : (obj.data?.objectId || Math.random());
    if (seen.has(key)) return sum;
    seen.add(key);
    return sum + (obj.usdValue || 0);
  }, 0);

  const icon = isToiletMode ? '🚽' : '🛡️';

  return (
    <div
      className={`protected-modal-overlay ${isToiletMode ? 'toilet-mode' : ''}`}
      onClick={onClose}
    >
      <div
        className={`protected-modal ${isToiletMode ? 'toilet-mode' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <h2 className="protected-modal-title">{icon} Auto-Protected Assets</h2>
        <p className="protected-modal-subtitle">
          {isToiletMode
            ? 'These assets are auto-protected from the flush!'
            : "Auto-protected for your safety — Distil won't touch these"
          }
        </p>
        <p className="protected-modal-note">
          💡 Manage your manually whitelisted objects via the <strong>Whitelist</strong> button in the header.
        </p>

        <div className="protected-categories">
          {groupedAssets.safeValue.length > 0 && (
            <AssetCategory title="💰 Safe Tokens with Value" count={groupedAssets.safeValue.length} assets={groupedAssets.safeValue} explanation="Real tokens with actual value — not penny dust 💰" />
          )}
          {groupedAssets.lpTokens.length > 0 && (
            <AssetCategory title="🏦 LP Tokens" count={groupedAssets.lpTokens.length} assets={groupedAssets.lpTokens} explanation="LP tokens = Your money working for you! 💎" />
          )}
          {groupedAssets.staking.length > 0 && (
            <AssetCategory title="💎 Staking Positions" count={groupedAssets.staking.length} assets={groupedAssets.staking} explanation="Staked = Making you money right now ⚡" />
          )}
          {groupedAssets.other.length > 0 && (
            <AssetCategory title="🔒 Other Valuable Objects" count={groupedAssets.other.length} assets={groupedAssets.other} explanation="DeFi stuff that's worth actual money 🏦" />
          )}
          {groupedAssets.nativeSui.length > 0 && (
            <AssetCategory title="⚡ Native SUI" count={groupedAssets.nativeSui.length} assets={groupedAssets.nativeSui} explanation="Your SUI balance (obviously protected!)" />
          )}
          {groupedAssets.distilObject.length > 0 && (
            <AssetCategory title="💎 Distil Protocol Objects" count={groupedAssets.distilObject.length} assets={groupedAssets.distilObject} explanation="Distil Protocol objects (protected automatically)" />
          )}
          {allGrouped.length === 0 && (
            <div className="protected-empty">
              <span>✨ No auto-protected assets detected</span>
            </div>
          )}
        </div>

        {totalValue > 0 && (
          <div className="protected-summary">
            <strong>Total Protected Value:</strong>
            <span className="protected-value">
              ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}

        <button onClick={onClose} className="protected-close-btn">Close</button>
      </div>
    </div>
  );
}

// ─── Asset Row ─────────────────────────────────────────────────────────────
function AssetRow({ asset, idx }) {
  const name = getAssetName(asset);
  const subtitle = getAssetSubtitle(asset);
  const iconUrl = getAssetIcon(asset);
  const usd = formatUsd(asset.usdValue);
  // First letter for placeholder — use symbol for coins, first word of name for others
  const placeholderChar = (asset.symbol || asset.enriched?.symbol || name || '?')[0].toUpperCase();

  return (
    <div className="protected-asset-item">
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className="asset-token-icon"
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
          }}
        />
      ) : null}
      <div
        className="asset-token-icon asset-token-icon--placeholder"
        style={{ display: iconUrl ? 'none' : 'flex' }}
      >
        {placeholderChar}
      </div>
      <div className="asset-info">
        <span className="asset-name">
          {name}
          {asset.isGroup && asset.objectCount > 1 && (
            <span className="asset-group-badge">{asset.objectCount}</span>
          )}
        </span>
        {subtitle && <span className="asset-balance">{subtitle}</span>}
      </div>
      {usd && <span className="asset-usd">{usd}</span>}
    </div>
  );
}

// ─── Asset Category ────────────────────────────────────────────────────────
function AssetCategory({ title, count, assets, explanation }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="asset-category">
      <div className="category-header" onClick={() => setExpanded(!expanded)}>
        <span className="category-title">{title} ({count})</span>
        <span className="category-toggle">{expanded ? '▼' : '▶'}</span>
      </div>
      <p className="category-explanation">{explanation}</p>
      {expanded && (
        <div className="category-assets">
          {assets.slice(0, 10).map((asset, idx) => (
            <AssetRow key={asset.coinType || asset.data?.objectId || idx} asset={asset} idx={idx} />
          ))}
          {assets.length > 10 && <div className="category-more">+ {assets.length - 10} more...</div>}
        </div>
      )}
    </div>
  );
}

export default ProtectedAssetsModal;
